import { timeTillExpiry } from '@/helpers/CartHelpers'
import { authenticationMethod } from '@/helpers/Environment'
import type { OTPAuthMethod } from '@/helpers/OTP'

import { TixTime } from '@/TixTime/TixTime'
import { API } from '@/api/API'
import { fetchOwnCart } from '@/api/Cart'
import { logInTrackJS } from '@/errors/helpers'
import store from '@/store/store'
import type { AuthenticationMethodName } from '@/types/Env'

export function loginAndTransferCart(
  credentials: Dictionary,
  method: AuthenticationMethodName | OTPAuthMethod | undefined = authenticationMethod,
): Promise<void> {
  const cart = store.getters['Cart/cart']

  const parameters = {
    ...credentials,
    method,
    // Transfer any existing cart to logged in user.
    cart_id: cart?.id,
  }

  return store.dispatch('Member/login', parameters).then(() => {
    if (cart) {
      // TODO Update only the parts that have changed.
      return refreshStoredCart()
    }
  })
}

export function refreshStoredCart(): Promise<void> {
  const id = store.getters['Cart/cart'].id
  return fetchOwnCart(id)
    .then((response) => {
      // Ignore stale refresh if the cart was cleared or replaced while this request was in flight (e.g. afterCheckoutRoute abandon-clear racing initializeCartStore).
      if (store.getters['Cart/cart']?.id !== id) {
        logInTrackJS('refreshStoredCart: ignoring stale success response', {
          refreshedCartId: id,
          currentCartId: store.getters['Cart/cart']?.id,
        })
        return
      }

      const cart = response.cart._data[0]
      const tickets = response.ticket._data

      if (cart.state !== 'pending') {
        return store.dispatch('Cart/clearAll')
      } else if (tickets.length > 0) {
        store.commit('Cart/apiResponse', response)
      } else {
        // Clear the cart if it is now empty.
        // This also clears any associated expiry or giftee.
        return deleteCart(cart.id)
      }
    })
    .catch((error) => {
      // Same stale-response guard as above — do not clear/replace a cart that is no longer the one we refreshed.
      if (store.getters['Cart/cart']?.id !== id) {
        logInTrackJS('refreshStoredCart: ignoring stale error response', {
          refreshedCartId: id,
          currentCartId: store.getters['Cart/cart']?.id,
        })
        return
      }

      if (isSellerScopeError(error)) {
        return deleteCart(id)
      } else if (error.response?.status === 404 || isStripeGatewayNotConfiguredError(error)) {
        return store.dispatch('Cart/clearAll')
      } else {
        throw error
      }
    })
}

/**
 *  A stripe_gateway not_configured error occurs when removing the seller prefix on the checkout page when there is no
 *  stripe account setup for the default. This will cause the cart to not be refreshed so need to clear it to stop the
 *  error as reloading the page won't get rid of it.
 */
function isStripeGatewayNotConfiguredError(error): boolean {
  return (
    error.response?.status === 400 &&
    error.response?.data._data[0]._context === 'stripe_gateway' &&
    error.response?.data._data[0]._code === 'not_configured'
  )
}

/**
 *  A scope_error error occurs when adding an item to the cart from one seller and then navigating to another seller's
 *  prefixed url.
 */
function isSellerScopeError(error): boolean {
  return error.response.status === 400 && error.response?.data._data[0]._code === 'scope_error'
}

export function cartExpired() {
  // This can not be a store getter because it is impure;
  // The return value changes depending on the current time.
  const localExpiry: number | null = store.getters['Cart/localExpiry']
  if (localExpiry) {
    try {
      return new TixTime(localExpiry).isBefore(new TixTime())
    } catch (e) {
      return false
    }
  } else {
    return false
  }
}

// How many seconds before cart expiry should the extend-cart-timer modal show.
export const cartExpiryAlertThreshold = 60

export function shouldAlertAboutCartExpiry() {
  const localExpiry = store.getters['Cart/localExpiry']
  const declinedCartTimerExtension = store.getters['Cart/declinedCartTimerExtension']
  if (!localExpiry || cartExpired() || declinedCartTimerExtension) {
    return false
  }

  const duration = timeTillExpiry(localExpiry).asSeconds()
  return duration <= cartExpiryAlertThreshold
}

export function cartExpiresIn() {
  const expiry = store.getters['Cart/localExpiry']
  return expiry ? timeTillExpiry(expiry) : null
}

export function deleteCart(id: string): Promise<void> {
  return API.delete(`my/cart/${id}`)
    .catch((error) => {
      // Ignore cart not found errors and assume it has already been deleted.
      if (error.response?.data._data[0]._code !== 'not_found') {
        throw error
      }
    })
    .then(() => store.dispatch('Cart/clearAll'))
}
